import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, cents } from "../lib/api";
import { useCart } from "../context/CartContext";
import { AddinBadge } from "../components/ProductCard";
import usePageMeta from "../hooks/usePageMeta";
import { track } from "../lib/analytics";
import { PRODUCT_GUIDES, bySlug as articleBySlug } from "../content/articles";
import { ReviewsSection, Stars, useReviews } from "../components/Reviews";
import { BottleImg } from "../components/BottleImg";
import { MAX_BOTTLES } from "../lib/pricing";

const SEO = {
  "tropical-recovery": ["Pineapple Mango Protein Juice: TROPICAL RECOVERY | Tiff Wellness Co.", "Tropical Recovery blends pineapple, mango, green apple and lime with clear protein for after training. See ingredients, pricing and Dallas pickup.", "Tropical Recovery — Pineapple & Mango Juice with Clear Protein"],
  "hydrate-plus": ["Watermelon Protein Juice: HYDRATE+ | Tiff Wellness Co.", "Hydrate+ pairs water-rich watermelon, pineapple and lime with clear protein for hydration and post-workout refreshment. Ingredients, prices and Dallas pickup.", "Hydrate+ — Watermelon Juice with Clear Protein"],
  "electric-sun": ["Blue Spirulina Protein Juice: ELECTRIC SUN | Tiff Wellness Co.", "Electric Sun is a vibrant blue blend of green apple, cucumber, pineapple, lemon and blue spirulina with clear protein. Ingredients and Dallas pickup.", "Electric Sun — Blue Spirulina Juice with Clear Protein"],
  "flow-plus": ["Beet Juice with Creatine: FLOW+ | Tiff Wellness Co.", "Flow+ combines beet, red apple, lemon and ginger with creatine for strength and high-intensity training days. See ingredients and Dallas pickup options.", "Flow+ — Beet Juice with Creatine"],
  "citrus-drive": ["Citrus Juice with BCAAs: CITRUS DRIVE | Tiff Wellness Co.", "Citrus Drive blends orange, pineapple, carrot and ginger with BCAAs for active days. View ingredients, pricing and available Dallas pickup.", "Citrus Drive — Orange & Carrot Juice with BCAAs"],
  "renew-plus": ["Cucumber & Kiwi Juice: RENEW+ | Tiff Wellness Co.", "Renew+ is a crisp traditional green juice — cucumber, green apple, kiwi, lemon and lime with no added supplement. $10 single, Dallas pickup available.", "Renew+ — Cucumber & Kiwi Green Juice (No Added Supplement)"],
};

const CONTENT = {
  "hydrate-plus": {
    about: "A refreshing, naturally sweet blend of water-rich fruit — made for hydration and for supporting protein intake after a workout.",
    notes: [
      ["Watermelon", "over 90% water — a naturally refreshing, hydrating base"],
      ["Pineapple", "a source of vitamin C"],
      ["Lime", "citrus brightness and vitamin C"],
      ["Clear protein", "protein contributes to muscle maintenance as part of your overall diet; the per-bottle amount is pending owner confirmation"],
    ],
    goals: ["Post-workout refreshment", "Daily hydration", "Supporting protein intake"],
  },
  "citrus-drive": {
    about: "A citrus-forward blend made for active days.",
    notes: [
      ["Orange", "a well-known source of vitamin C"],
      ["Pineapple", "a source of vitamin C"],
      ["Carrot", "contains beta-carotene"],
      ["Ginger", "bright flavor; many people enjoy it for digestive comfort"],
      ["BCAAs", "essential amino acids involved in muscle protein production — no recovery outcome is guaranteed"],
    ],
    goals: ["Active days", "Everyday vitamin C", "Training support"],
  },
  "flow-plus": {
    about: "Earthy and refreshing — built around beets for strength and high-intensity training days.",
    notes: [
      ["Beet", "contains naturally occurring nitrates"],
      ["Red apple", "natural carbohydrates"],
      ["Lemon", "a source of vitamin C"],
      ["Ginger", "a bright, warming finish"],
      ["Creatine", "creatine supports short, intense activity when consistently consumed in effective amounts — the amount in this bottle is pending owner confirmation, so no dosage claim is made"],
    ],
    goals: ["Strength training", "High-intensity days", "Performance-focused routines"],
  },
  "tropical-recovery": {
    about: "A tropical fruit, carbohydrate and protein combination for after training.",
    notes: [
      ["Pineapple", "a source of vitamin C"],
      ["Mango", "contains vitamins A and C"],
      ["Green apple", "natural carbohydrates"],
      ["Lime", "citrus vitamin C"],
      ["Clear protein", "protein supports muscle repair and maintenance as part of your overall diet"],
    ],
    goals: ["Post-workout", "Protein support", "Refuel after training"],
  },
  "renew-plus": {
    about: "A crisp green juice with no added supplement.",
    notes: [
      ["Cucumber", "water-rich and hydrating"],
      ["Green apple", "natural carbohydrates"],
      ["Kiwi", "a source of vitamin C"],
      ["Lemon & lime", "citrus vitamin C"],
    ],
    goals: ["Everyday refresh", "Light hydration", "A no-supplement option"],
  },
  "electric-sun": {
    about: "A vibrant blue refresher — fruit, hydration and naturally occurring plant compounds.",
    notes: [
      ["Green apple", "natural carbohydrates"],
      ["Cucumber", "water-rich and hydrating"],
      ["Pineapple", "a source of vitamin C"],
      ["Lemon", "citrus vitamin C"],
      ["Blue spirulina", "contains phycocyanin, a naturally occurring plant pigment — we don't claim proven effects for this drink"],
      ["Clear protein", "supports daily protein intake and muscle maintenance as part of your diet"],
    ],
    goals: ["Daily refreshment", "Antioxidant-minded routines", "Protein support"],
  },
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const { add, setDrawerOpen } = useCart();
  const { data: reviews, reload: reloadReviews } = useReviews(slug);
  const seo = SEO[slug];
  const origin = window.location.origin;
  usePageMeta(seo ? seo[0] : "Tiff. Wellness Co.", seo ? seo[1] : "", {
    ogImage: p ? p.image : undefined,
    jsonLd: p ? [
      {
        "@context": "https://schema.org", "@type": "Product",
        "@id": `${origin}/products/${p.slug}#product`,
        name: p.name, image: `${origin}${p.image}`, url: `${origin}/products/${p.slug}`,
        description: `${p.ingredients.join(", ")}${p.functional_addition ? `, with added ${p.functional_addition}` : ""}. 12 fl oz / 355 mL cold-pressed juice.`,
        brand: { "@type": "Brand", name: "tiff. wellness co." },
        offers: { "@type": "Offer", price: (p.price_cents / 100).toFixed(2), priceCurrency: "USD",
                  url: `${origin}/products/${p.slug}`,
                  ...(p.live_checkout_enabled && p.available ? { availability: "https://schema.org/InStock" } : {}) },
        ...(reviews?.count > 0 ? {
          aggregateRating: { "@type": "AggregateRating", ratingValue: reviews.average, reviewCount: reviews.count, bestRating: 5, worstRating: 1 },
          review: reviews.reviews.slice(0, 5).map((r) => ({
            "@type": "Review", author: { "@type": "Person", name: r.author },
            reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
            ...(r.text ? { reviewBody: r.text } : {}), datePublished: r.created_at.slice(0, 10),
          })),
        } : {}),
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "Shop", item: `${origin}/shop` },
          { "@type": "ListItem", position: 3, name: p.name, item: `${origin}/products/${p.slug}` },
        ],
      },
    ] : null,
  });

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => { setP(r.data); track("view_item", { item: slug }); }).catch(() => setP(false));
    api.get("/products").then((r) => setRelated(r.data.filter((x) => x.slug !== slug).slice(0, 3)));
    window.scrollTo(0, 0);
  }, [slug]);

  if (p === false) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><h1 className="text-3xl mb-3">Juice not found</h1><Link className="underline" to="/shop">Back to shop</Link></div>;
  if (!p) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-sm">Loading…</div>;

  const desc = `${p.ingredients.map((i, idx) => idx === 0 ? i[0].toUpperCase() + i.slice(1) : i).join(", ").replace(/, ([^,]*)$/, " and $1")}${p.functional_addition ? `, with added ${p.functional_addition}` : ""}.`;

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <nav aria-label="Breadcrumb" className="text-xs text-[#171715]/60 mb-6">
        <Link to="/" className="underline">Home</Link> / <Link to="/shop" className="underline">Shop</Link> / <span>{p.name}</span>
      </nav>
      <div className="grid md:grid-cols-2 gap-10">
        <div className="photo-panel self-start w-full aspect-[4/5] max-h-[540px] p-5 sm:p-8 flex justify-center items-center overflow-hidden">
          <BottleImg src={p.image} alt={p.image_alt} width="320" height="427" loading="eager" sizes="(max-width: 768px) 85vw, 480px"
            pictureClassName="block w-full h-full min-h-0 flex items-center justify-center"
            className="max-w-full max-h-full w-auto h-auto object-contain" />
        </div>
        <div>
          <h1 className="text-4xl sm:text-5xl">{seo ? seo[2] : p.name}</h1>
          <p className="text-sm text-[#171715]/60 mt-1">12 fl oz / 355 mL · cold-pressed, made fresh to order</p>
          <p className="font-mono text-xl mt-3" data-testid="product-price">{cents(p.price_cents)}</p>
          <p className="text-xs text-[#171715]/60 mt-1" data-testid="product-pack-pricing">
            {p.category === "traditional"
              ? "$10 single · traditional 3-pack and 6-pack pricing pending confirmation (multiples charged at the single price for now)"
              : "$12 single · $28 per 3 · $54 per 6 — mix & match functional flavors, best pack pricing applied automatically"}
          </p>
          {reviews?.count > 0 && (
            <p className="mt-2 flex items-center gap-2 text-sm text-[#171715]/70" data-testid="product-review-stars">
              <Stars value={reviews.average} /> <span className="font-mono">{reviews.average}</span> ({reviews.count})
            </p>
          )}
          <p className="mt-4 text-[#171715]/80">{desc}</p>
          {p.goal && <p className="mt-2 text-sm font-mono uppercase text-[#A35847]" data-testid="product-goal">{p.goal}</p>}
          {CONTENT[slug] && <p className="mt-3 text-sm text-[#171715]/75 leading-relaxed" data-testid="product-about">{CONTENT[slug].about}</p>}
          <div className="mt-3"><AddinBadge addition={p.functional_addition} slug={p.slug} /></div>
          {p.category === "traditional" && <p className="mt-2 text-xs font-mono uppercase text-[#A35847]" data-testid="product-traditional-badge">Traditional — no added supplement</p>}
          <div className="mt-6 card-paper p-4 text-sm space-y-2">
            <p className="font-medium">Ingredients (as listed on the menu)</p>
            <p className="capitalize text-[#171715]/80">{p.ingredients.join(", ")}{p.functional_addition ? ` + ${p.functional_addition}` : ""}</p>
            {CONTENT[slug] && (
              <ul className="space-y-1.5 pt-1" data-testid="product-ingredient-notes">
                {CONTENT[slug].notes.map(([ing, note]) => (
                  <li key={ing} className="text-xs text-[#171715]/70"><span className="font-medium text-[#171715]">{ing}:</span> {note}</li>
                ))}
              </ul>
            )}
            <p className="text-xs text-[#171715]/55">Nutrition facts and allergens will be published once confirmed.</p>
          </div>
          {CONTENT[slug] && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="product-goals">
              {CONTENT[slug].goals.map((g) => <span key={g} className="text-xs border border-[#EBD9D0] bg-white px-2.5 py-1.5">{g}</span>)}
            </div>
          )}
          {p.storage_guidance && (
            <div className="mt-4 card-paper p-4 text-sm" data-testid="product-storage">
              <p className="font-medium mb-1">Freshness &amp; storage</p>
              <p className="text-xs text-[#171715]/70 leading-relaxed">{p.storage_guidance}</p>
            </div>
          )}
          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-[#d6cfc2] bg-white">
              <button data-testid="product-qty-dec" aria-label="Decrease quantity" className="min-h-[44px] min-w-[44px]" onClick={() => setQty(Math.max(1, qty - 1))}>−</button>
              <span className="w-10 text-center font-mono" data-testid="product-qty-value">{qty}</span>
              <button data-testid="product-qty-inc" aria-label="Increase quantity" className="min-h-[44px] min-w-[44px]" onClick={() => setQty(Math.min(MAX_BOTTLES, qty + 1))}>+</button>
            </div>
            <button data-testid={`product-detail-add-to-cart-${p.slug}`} className="btn-ink flex-1" disabled={!p.available}
              onClick={() => { add(p.slug, qty); track("add_to_cart", { item: p.slug }); toast.success(`${p.name} ×${qty} added`); setDrawerOpen(true); }}>
              Add to stack — one-time
            </button>
          </div>
          <Link to="/subscriptions" data-testid="product-detail-subscribe-link" className="btn-ghost w-full mt-3">Prefer recurring pickup? See subscription plans</Link>
          <p className="text-xs text-[#171715]/55 mt-3">One-time and recurring purchases are separate, explicit choices — we never convert a one-time cart into a subscription.</p>
          <div className="mt-8 space-y-3">
            <details className="card-paper p-4"><summary className="cursor-pointer text-sm font-medium">How does pickup work?</summary><p className="text-sm text-[#171715]/75 mt-2">Choose SandersFit Dallas or South Side on Lamar and a pickup day at checkout — Monday, Wednesday and Thursday evenings, or Tuesday and Friday all day. Orders need at least 24 hours' notice; pickup is subject to availability, and we coordinate your exact time after your order is confirmed. Dallas–Fort Worth delivery is available by arrangement.</p></details>
            <details className="card-paper p-4"><summary className="cursor-pointer text-sm font-medium">Does this juice include extra add-ins?</summary><p className="text-sm text-[#171715]/75 mt-2">{p.functional_addition ? `${p.name} already includes ${p.functional_addition} — we never double-dose a formula that already includes it.` : `${p.name} is a fruit and vegetable blend with no functional addition listed.`} Optional custom add-ins are disabled until doses, compatibility and surcharges are confirmed by the owner.</p></details>
          </div>
        </div>
      </div>
      <ReviewsSection slug={slug} data={reviews} reload={reloadReviews} />
      <section className="mt-16">
        <h2 className="text-2xl mb-5">You might also like</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {related.map((r) => (
            <Link key={r.slug} to={`/products/${r.slug}`} data-testid={`related-product-${r.slug}`} className="card-paper p-4 flex items-center gap-4 hover:border-[#A35847]/50">
              <span className="photo-panel p-1.5 shrink-0 flex items-center justify-center"><img src={r.image.replace(/-800\.(jpg|png)$/, "-480.webp")} alt="" width="48" height="64" loading="lazy" className="h-16 w-auto object-contain" /></span>
              <span><span className="block font-medium text-sm">{r.name}</span><span className="text-xs text-[#171715]/60 font-mono">{cents(r.price_cents)}</span></span>
            </Link>
          ))}
        </div>
      </section>
      {PRODUCT_GUIDES[slug]?.length > 0 && (
        <section className="mt-10" data-testid="product-guides-section">
          <h2 className="text-xl font-medium mb-4">Guides from the journal</h2>
          <ul className="space-y-2">
            {PRODUCT_GUIDES[slug].map((gs) => {
              const g = articleBySlug[gs];
              return g ? (
                <li key={gs}>
                  <Link to={`/journal/${gs}`} data-testid={`product-guide-link-${gs}`} className="underline text-sm decoration-[#A35847]/50">{g.title}</Link>
                </li>
              ) : null;
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
